import type { ReactNode } from 'react';
import type { ProductListViewModel } from 'autogen/swagger/all';
import InventoryLaneSection from 'components/connectedContent/inventoryLaneConnectedContent';
import FollowButton from 'components/FollowButton';
import FollowButtonWithTooltip from 'components/FollowButton/FollowButtonWithTooltip';
import { Typography } from 'components/generic/typography';
import Layout from 'components/Layout';
import FlexRow from 'components/Layout/components/Flex/components/FlexRow';
import RevieveMakeupAdvisor from 'components/RevieveMakeupAdvisor';
import ProductListWithFilters from 'components/shared/productList/ProductListWithFilters';
import { useGetProductListPartial } from 'components/shared/productList/shared/hooks/useGetProductListPartialQuery';
import ConnectedContentTopBanner from 'components/surveySystem/components/ConnectedContentTopBanner';
import format from 'helpers/translations';
import TrackingContext from 'modules/tracking/GA4/helpers/TrackingContext';
import ProductListPageHeader from './components/ProductListPageHeader/ProductListPageHeader';

type ProductListPageType = {
    model: ProductListViewModel;
    hideProductList?: boolean;
    contentOverPLP?: ReactNode;
    contentUnderPLP?: ReactNode;
    SEOContentBlock?: ReactNode;
};

export default function ProductListPage({
    model: {
        productListModel: initialModel,
        seoText,
        pageHeader,
        makeupAdvisorConfig,
        productGroup,
        followBrandClubModel
    },
    contentOverPLP,
    contentUnderPLP,
    SEOContentBlock,
    hideProductList = false
}: ProductListPageType) {
    const { data: model } = useGetProductListPartial(initialModel);
    const { productList } = model;
    // Temporary Revieve Makeup Advisor implementation
    const allowedPLPs = ['Makeup', 'Ansigt', 'Øjne', 'Læber', 'Mini makeup', 'Makeup til mænd'];
    const showMakeupAdvisor = makeupAdvisorConfig && productGroup && allowedPLPs.includes(productGroup.name);

    return (
        <TrackingContext context="product_list">
            {followBrandClubModel && (
                <Layout.Section>
                    <FlexRow justifyContent="end">
                        {followBrandClubModel.isFollowing ? (
                            <FollowButton {...followBrandClubModel} />
                        ) : (
                            <FollowButtonWithTooltip {...followBrandClubModel} />
                        )}
                    </FlexRow>
                </Layout.Section>
            )}
            <Layout.Page>
                {/* FullWidth Connected Content - where position has been set to top. (Yes i know it is weird that we render a list-item outside of the actual list. but that was the decision) */}
                {productList.connectedContentTopBanner && (
                    <ConnectedContentTopBanner model={productList.connectedContentTopBanner} />
                )}

                {/* Page Header */}
                {pageHeader && <ProductListPageHeader model={pageHeader} />}

                {/* contentOverPLP slot */}
                {contentOverPLP && <>{contentOverPLP}</>}

                {/* InventoryLane */}
                {productList.connectedContent && <InventoryLaneSection model={productList.connectedContent} />}

                {/* Product List with filters  */}
                {!hideProductList && (
                    <Layout.Section>
                        {/* Temporary Revieve Makeup Advisor implementation */}
                        {showMakeupAdvisor && makeupAdvisorConfig && (
                            <RevieveMakeupAdvisor config={makeupAdvisorConfig} />
                        )}
                        <ProductListWithFilters model={model} />
                    </Layout.Section>
                )}

                {/* contentUnderPLP slot */}
                {contentUnderPLP && <>{contentUnderPLP}</>}

                {/* SEO text */}
                {SEOContentBlock && <>{SEOContentBlock}</>}
                {seoText && !SEOContentBlock && (
                    <Layout.Section>
                        <Typography.Body as="span">{format(seoText)}</Typography.Body>
                    </Layout.Section>
                )}
            </Layout.Page>

            {/* Recommendation */}
            {/* {productGroupRecommendations && (
                <Layout.Section>
                    <RecommendationLoader model={productGroupRecommendations} />
                </Layout.Section>
            )} */}
        </TrackingContext>
    );
}
